<template>
  <div class="wg-ctrysel" v-click-outside="close">
    <button type="button" class="wg-select wg-ctrysel-btn" :disabled="!canEdit" @click="toggleOpen">
      <span class="lbl">{{ buttonLabel }}</span>
      <i class="material-icons chev">expand_more</i>
    </button>
    <div v-if="open" class="wg-ctrysel-menu">
      <input
        ref="search"
        class="wg-input-sm wg-ctrysel-q"
        placeholder="Search countries"
        v-model="query"
      />
      <div class="wg-ctrysel-list">
        <button type="button" class="wg-ctrysel-item" :class="{ active: !selected.length }" @click="clearAll">
          <span class="wg-check" :class="{ on: !selected.length }">{{ !selected.length ? '✓' : '' }}</span>
          <span>Worldwide (all countries)</span>
        </button>
        <button
          v-for="country in filteredCountries"
          :key="country.code"
          type="button"
          class="wg-ctrysel-item"
          :class="{ active: isSelected(country) }"
          @click="$emit('toggle', country)"
        >
          <span class="wg-check" :class="{ on: isSelected(country) }">{{ isSelected(country) ? '✓' : '' }}</span>
          <span>{{ country.name }}</span>
        </button>
        <div v-if="filteredCountries.length === 0" class="wg-ctrysel-none">
          No countries match "{{ query }}"
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Reporting-countries multi-select (design_updated5 CountrySelect):
 * Worldwide by default, searchable country checkboxes, selections combined.
 */
import dataForSeoLocations from '@/consts/dataforseo_locations.json'

const COUNTRIES = (dataForSeoLocations.locations || dataForSeoLocations)
  .filter((row) => row.location_type === 'Country')
  .map((row) => ({ code: String(row.location_code), name: row.location_name }))
  .sort((a, b) => a.name.localeCompare(b.name))

export default {
  name: 'WinsCountrySelect',
  props: {
    /** Selected DataForSEO country location codes. */
    selected: {
      type: Array,
      default: () => [],
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      open: false,
      query: '',
      countries: COUNTRIES,
    }
  },
  computed: {
    buttonLabel () {
      if (!this.selected.length) return 'Worldwide (all countries)'
      const byCode = new Map(this.countries.map((c) => [c.code, c.name]))
      return this.selected.map((code) => byCode.get(code) || code).join(', ')
    },
    filteredCountries () {
      const q = this.query.trim().toLowerCase()
      if (!q) return this.countries
      return this.countries.filter((c) => c.name.toLowerCase().includes(q))
    },
  },
  methods: {
    isSelected (country) {
      return this.selected.includes(country.code)
    },
    toggleOpen () {
      if (!this.canEdit) return
      this.open = !this.open
      this.query = ''
      if (this.open) {
        this.$nextTick(() => {
          if (this.$refs.search) this.$refs.search.focus()
        })
      }
    },
    close () {
      this.open = false
    },
    clearAll () {
      this.close()
      if (this.selected.length) this.$emit('clear')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './settings-card.scss';

.wg-ctrysel {
  position: relative;
  max-width: 420px;
}

.wg-ctrysel-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  /* .wg-select reserves 40px for the wrap's absolute chevron; ours is inline */
  padding-right: 13px;

  .lbl {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chev {
    flex: none;
    color: $pop-ink-faint;
  }

  &:disabled {
    cursor: default;
    background: $pop-box-bg;
  }
}

.wg-ctrysel-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid $wg-panel-line;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(15, 45, 55, 0.14);
  padding: 10px;
  z-index: 40;
}

.wg-ctrysel-q {
  width: 100%;
  margin-bottom: 8px;
}

.wg-ctrysel-list {
  max-height: 240px;
  overflow-y: auto;
}

.wg-ctrysel-item {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  padding: 8px 10px;
  border-radius: 7px;
  font-size: 0.88rem;
  color: $pop-teal;
  cursor: pointer;

  &:hover {
    background: $pop-box-bg;
  }

  &.active {
    font-weight: 700;
  }
}

.wg-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1.5px solid $wg-panel-line;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  margin-right: 8px;
  flex: none;

  &.on {
    background: $pop-success;
    border-color: $pop-success;
  }
}

.wg-ctrysel-none {
  font-size: 0.82rem;
  color: $pop-ink-faint;
  padding: 10px 12px;
}
</style>
